import React, { useState } from "react";
import AppStoreLogo from "../../AppStore.svg";
import { makeStyles } from "@material-ui/styles";
import Button from "@material-ui/core/Button";
import GooglePlayBadge from "../../google-play-badge.png";
import ProjectDialog from "./ProjectDialog";
import Typography from "@material-ui/core/Typography";
import { PROGRAM_FULL_NAME } from "../../brand/config";

import backdrop from "../../images/backdrop-clipped.jpg";
/** Same artwork as the shipped app icon (sync from `MobileApp/.../AppIcon-GearLogbook.appiconset/1024.png`). */
import appStoreIcon from "../../images/gearlogbook-app-icon.png";
import networkLogo from "../../images/network-logo.png";
import partnerPcffa from "../../images/partners/pcffa.webp";
import partnerTnc from "../../images/partners/tnc.webp";
import partnerOpc from "../../images/partners/opc.webp";
import partnerCdfw from "../../images/partners/cdfw.webp";

const OCEAN_BLUE = "#1565a8";
const WAVE_H = 40;
const WAVE_W = 1200;
const WAVE_MID = WAVE_H / 3;
const WAVE_AMP = 8;

const wavePath = [
  `M 0 ${WAVE_MID}`,
  `Q ${WAVE_W * 0.125} ${WAVE_MID - WAVE_AMP} ${WAVE_W * 0.25} ${WAVE_MID}`,
  `Q ${WAVE_W * 0.375} ${WAVE_MID + WAVE_AMP} ${WAVE_W * 0.5} ${WAVE_MID}`,
  `Q ${WAVE_W * 0.625} ${WAVE_MID - WAVE_AMP} ${WAVE_W * 0.75} ${WAVE_MID}`,
  `Q ${WAVE_W * 0.875} ${WAVE_MID + WAVE_AMP} ${WAVE_W} ${WAVE_MID}`,
  `L ${WAVE_W} ${WAVE_H}`,
  `L 0 ${WAVE_H}`,
  "Z",
].join(" ");

/** Primary host — same role as `sessionHostLogo` on the app WelcomePage. */
const HOST_PARTNER = {
  key: "pcffa",
  src: partnerPcffa,
  maxHeight: 152,
  alt: "Pacific Coast Federation of Fishermen's Associations",
  href: "https://www.pcffa.org",
};

/** Shown below PCFFA (PCFFA is the host in `HOST_PARTNER`). */
const OTHER_PROGRAM_PARTNERS = [
  {
    key: "tnc",
    src: partnerTnc,
    maxHeight: 56,
    alt: "The Nature Conservancy",
    href: "https://www.nature.org",
  },
  {
    key: "opc",
    src: partnerOpc,
    maxHeight: 52,
    alt: "California Ocean Protection Council",
    href: "https://opc.ca.gov",
  },
  {
    key: "cdfw",
    src: partnerCdfw,
    maxHeight: 58,
    alt: "California Department of Fish and Wildlife",
    href: "https://wildlife.ca.gov",
  },
];

const SIGHTINGS_FORM_URL = "https://ee.kobotoolbox.org/xqi6e2Se/";

const useStyles = makeStyles(theme => ({
  page: {
    width: "100%",
    minHeight: "100vh",
    backgroundColor: OCEAN_BLUE,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  hero: {
    position: "relative",
    minHeight: "min(52vh, 340px)",
    backgroundImage: `url(${backdrop})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(5, 2, 6),
  },
  scrim: {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(255, 255, 255, 0.55)",
    pointerEvents: "none",
  },
  heroLogoWrap: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    maxWidth: 720,
    paddingLeft: theme.spacing(2),
  },
  heroLogo: {
    width: "min(100%, 640px)",
    height: "auto",
    maxHeight: 200,
    objectFit: "contain",
  },
  waveRow: {
    marginTop: -WAVE_H / 1.5,
    zIndex: 2,
    position: "relative",
    lineHeight: 0,
  },
  waveSvg: {
    display: "block",
    width: "100%",
    height: WAVE_H,
  },
  taglineBand: {
    backgroundColor: OCEAN_BLUE,
    marginTop: -WAVE_H / 2,
    paddingTop: WAVE_H / 2 + theme.spacing(2),
    paddingBottom: theme.spacing(6),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  tagline: {
    color: "#fff",
    fontSize: "clamp(1.1rem, 2.8vw, 1.45rem)",
    fontStyle: "italic",
    fontWeight: 700,
    fontFamily: '"Poppins", "Roboto", sans-serif',
    textAlign: "center",
    lineHeight: 1.35,
    maxWidth: 920,
    margin: "0 auto",
    "& u": {
      textDecoration: "underline",
      textUnderlineOffset: "3px",
    },
  },
  /** Centers the app row as one unit so it aligns with the centered tagline */
  appCtaShell: {
    marginTop: theme.spacing(5),
    paddingTop: theme.spacing(5),
    borderTop: "1px solid rgba(255, 255, 255, 0.22)",
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  appCta: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "24px",
    maxWidth: 640,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      gap: "4px",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      maxWidth: 400,
    },
  },
  appIcon: {
    // width: 88 * 1.25,
    // height: 88 * 1.25,
    width: 128,
    height: 128,
    borderRadius: 30,
    flexShrink: 0,
    objectFit: "cover",
    boxShadow: "0 6px 24px rgba(0, 0, 0, 0.18)",
    [theme.breakpoints.down("sm")]: {
      width: 80,
      height: 80,
    },
    margin: 16
  },
  appCtaBody: {
    flex: 1,
    minWidth: 0,
    textAlign: "left",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
    },
  },
  appCtaTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "clamp(1.15rem, 2.5vw, 1.4rem)",
    marginBottom: theme.spacing(1.25),
    lineHeight: 1.3,
    letterSpacing: "-0.01em",
  },
  appCtaDesc: {
    color: "rgba(255, 255, 255, 0.9)",
    fontSize: "0.95rem",
    lineHeight: 1.6,
    marginBottom: theme.spacing(1),
    maxWidth: 480,
    [theme.breakpoints.down("sm")]: {
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  storeBadges: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
    justifyContent: "flex-start",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
    },
  },
  /** Same rendered height for App Store + Google Play artwork (different aspect ratios → widths differ). */
  storeBadgeImg: {
    display: "block",
    height: 44,
    width: "auto",
  },
  actionsBand: {
    backgroundColor: "#fff",
    padding: theme.spacing(5, 3, 5.5),
    borderTop: "1px solid rgba(0, 0, 0, 0.06)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
  },
  actionsInner: {
    maxWidth: 560,
    margin: "0 auto",
    textAlign: "center",
  },
  sectionLabel: {
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontSize: "0.72rem",
    color: "#5c656e",
    marginBottom: theme.spacing(3),
  },
  actionsRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  /** Wraps each Button so spacing isn’t collapsed/overridden by MUI root styles */
  actionButtonWrap: {
    width: "100%",
    maxWidth: 320,
    display: "flex",
    justifyContent: "center",
    "&:not(:last-child)": {
      marginBottom: theme.spacing(3),
    },
  },
  actionButton: {
    width: "100%",
    maxWidth: 320,
    padding: theme.spacing(1.35, 2),
    fontSize: "0.9375rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    border: `1.5px solid ${OCEAN_BLUE}`,
    color: OCEAN_BLUE,
    backgroundColor: "#fff",
    lineHeight: 1.35,
    boxShadow: "none",
    transition: "background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(21, 101, 168, 0.05)",
      border: `1.5px solid ${OCEAN_BLUE}`,
      boxShadow: "0 2px 12px rgba(21, 101, 168, 0.12)",
    },
  },
  badgeLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.88,
    },
  },
  /** Gray strip matching WelcomePage: host block + partner carousel */
  partnersStrip: {
    backgroundColor: "#e8ebee",
  },
  hostSection: {
    padding: theme.spacing(4, 3),
    borderTop: "2px solid #1a1e21",
    textAlign: "center",
  },
  /** Stacked sponsor block: logo → label → name (reads cleanly; no short copy beside tall mark) */
  hostRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2.5),
    maxWidth: 560,
    margin: "0 auto",
    width: "100%",
  },
  hostTextBlock: {
    minWidth: 0,
    maxWidth: 480,
  },
  hostPresentedLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#5c6570",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginTop: 0,
    marginBottom: theme.spacing(1),
    lineHeight: 1.3,
  },
  hostOrgName: {
    fontSize: "clamp(1.1rem, 2.5vw, 1.4rem)",
    fontWeight: 600,
    color: "#1a1e21",
    lineHeight: 1.4,
    letterSpacing: "-0.01em",
    margin: 0,
  },
  hostLogoWrap: {
    lineHeight: 0,
  },
  hostLogoLink: {
    display: "inline-block",
    lineHeight: 0,
    maxWidth: 220,
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.88,
    },
    "&:focus-visible": {
      outline: `2px solid ${OCEAN_BLUE}`,
      outlineOffset: 4,
      borderRadius: 2,
    },
  },
  programPartnersSection: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    borderTop: "1px solid rgba(26, 30, 33, 0.12)",
    borderBottom: "2px solid #1a1e21",
  },
  programPartnersTitle: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: "#5c6570",
    marginBottom: theme.spacing(2),
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textAlign: "center",
  },
  partnersLogoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(2),
    alignItems: "center",
    justifyItems: "center",
    maxWidth: 720,
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  partnerLogoCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 72,
    padding: theme.spacing(1),
    minWidth: 0,
  },
  partnerLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.88,
    },
    "&:focus-visible": {
      outline: `2px solid ${OCEAN_BLUE}`,
      outlineOffset: 4,
      borderRadius: 2,
    },
  },
  partnerImg: {
    maxWidth: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
  },
  footer: {
    backgroundColor: OCEAN_BLUE,
    padding: theme.spacing(3, 2),
    textAlign: "center",
  },
  footerLink: {
    color: "rgba(255,255,255,0.92)",
    fontSize: "0.9rem",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    "&:hover": {
      color: "#fff",
    },
  },
}));

function RecoveryNetworkLanding() {
  const classes = useStyles();
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  return (
    <div className={classes.page}>
      <section className={classes.hero} aria-label="Program banner">
        <div className={classes.scrim} />
        <div className={classes.heroLogoWrap}>
          <img
            className={classes.heroLogo}
            src={networkLogo}
            alt={`${PROGRAM_FULL_NAME} logo`}
          />
        </div>
      </section>

      <div className={classes.waveRow} aria-hidden>
        <svg
          className={classes.waveSvg}
          viewBox={`0 0 ${WAVE_W} ${WAVE_H}`}
          preserveAspectRatio="none"
        >
          <path d={wavePath} fill={OCEAN_BLUE} />
        </svg>
      </div>

      <section className={classes.taglineBand} aria-labelledby="download-gearlogbook-heading">
        <Typography component="p" className={classes.tagline}>
          An <u>industry-led</u> collaboration of California fishermen working together to retrieve
          lost gear and keep the ocean clean.
        </Typography>

        <div className={classes.appCtaShell}>
          <div className={classes.appCta}>
            <img
              className={classes.appIcon}
              src={appStoreIcon}
              alt=""
              width={128}
              height={128}
            />
            <div className={classes.appCtaBody}>
              <Typography id="download-gearlogbook-heading" component="h2" className={classes.appCtaTitle}>
                Install Gear Logbook
              </Typography>
              <Typography className={classes.appCtaDesc} component="p">
                Get the network's mobile app for tracking gear recoveries at sea. The time, location, and tag ID's of your recoveries will be logged and shared with your port coordinator. Free on iOS and Android.
              </Typography>
              <div className={classes.storeBadges}>
                <a
                  className={classes.badgeLink}
                  href="https://apps.apple.com/us/app/gearlogbook/id6762100469"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                >
                  <img className={classes.storeBadgeImg} src={AppStoreLogo} alt="" />
                </a>
                <a
                  className={classes.badgeLink}
                  
                  href="https://play.google.com/store/apps/details?id=org.pcffa.gearlogbook"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                >
                  <img height={64} style={{height: 64}} className={classes.storeBadgeImg} src={GooglePlayBadge} alt="" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={classes.actionsBand}>
        <div className={classes.actionsInner}>
          <Typography className={classes.sectionLabel} component="p">
            Resources
          </Typography>
          <div className={classes.actionsRow}>
            <div className={classes.actionButtonWrap}>
              <Button
                color="inherit"
                variant="contained"
                className={classes.actionButton}
                disableElevation
                onClick={() => setProjectDialogOpen(true)}
              >
                Port Coordinator Login
              </Button>
            </div>
            <div className={classes.actionButtonWrap}>
              <Button
                color="inherit"
                component="a"
                href={SIGHTINGS_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                className={classes.actionButton}
                disableElevation
              >
                Lost Gear Sightings Form
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className={classes.partnersStrip}>
        <section className={classes.hostSection} aria-labelledby="presented-by-label">
          <div className={classes.hostRow}>
            <div className={classes.hostLogoWrap}>
              <a
                className={classes.hostLogoLink}
                href={HOST_PARTNER.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${HOST_PARTNER.alt} (opens in new tab)`}
              >
                <img
                  className={classes.partnerImg}
                  src={HOST_PARTNER.src}
                  alt=""
                  style={{ maxHeight: HOST_PARTNER.maxHeight }}
                />
              </a>
            </div>
            <div className={classes.hostTextBlock}>
              <Typography
                id="presented-by-label"
                component="p"
                className={classes.hostPresentedLabel}
                gutterBottom={false}
              >
                Presented By
              </Typography>
              <Typography component="p" className={classes.hostOrgName} gutterBottom={false}>
                {HOST_PARTNER.alt}
              </Typography>
            </div>
          </div>
        </section>

        <section className={classes.programPartnersSection} aria-labelledby="partners-heading">
          <Typography id="partners-heading" component="h2" className={classes.programPartnersTitle}>
            Program Partners
          </Typography>
          <div className={classes.partnersLogoGrid}>
            {OTHER_PROGRAM_PARTNERS.map(({ key, src, maxHeight, alt, href }) => (
              <div key={key} className={classes.partnerLogoCell}>
                <a
                  className={classes.partnerLink}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${alt} (opens in new tab)`}
                >
                  <img className={classes.partnerImg} src={src} alt="" style={{ maxHeight }} />
                </a>
              </div>
            ))}
          </div>
        </section>
      </div>

      <footer className={classes.footer}>
        <a className={classes.footerLink} href="/tos.html">
          Terms of Service
        </a>
      </footer>

      <ProjectDialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} />
    </div>
  );
}

export default RecoveryNetworkLanding;
